import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, fileUrl, KECAMATAN_MAGELANG } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PhotoUpload from "@/components/PhotoUpload";
import { Plus, Store, Pencil, Eye, TrendingUp, Save, X, MapPin, Calendar, Mail, MessageCircle, User } from "lucide-react";
import { toast } from "sonner";
import { PLATFORMS } from "@/components/SocialIcons";
import VerifiedBadge from "@/components/VerifiedBadge";

export default function MyProfile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState(user || {});
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => { setForm(user || {}); }, [user]);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/businesses/mine/list"); setMine(data); }
    catch (err) { console.error(err); }
  })(); }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const updSocial = (k) => (e) => setForm({ ...form, socials: { ...(form.socials || {}), [k]: e.target.value } });
  const cancelEdit = () => { setForm(user || {}); setEditMode(false); };
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.patch("/me", {
        name: form.name, username: form.username, whatsapp: form.whatsapp,
        address: form.address, kecamatan: form.kecamatan, city: form.city || "Magelang",
        bio: form.bio, birthdate: form.birthdate, instagram: form.instagram, photo_path: form.photo_path,
        socials: form.socials || {},
      });
      await refresh(); toast.success("Profil diperbarui"); setEditMode(false);
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  if (!form) return null;
  const u = user || {};

  return (
    <Layout>
      <div className="mb-8 flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-bizma-sage font-bold">Akun Anda</div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold mt-1">Profil Saya</h1>
        </div>
        {!editMode && (
          <button onClick={() => setEditMode(true)} className="btn-primary text-sm py-2 px-4" data-testid="edit-profile-btn"><Pencil size={14}/> Edit Profil</button>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {editMode ? (
        <form onSubmit={save} className="felt-card p-6 sm:p-8 lg:col-span-2 space-y-4" data-testid="my-profile-form">
          <div>
            <label className="text-sm font-medium">Foto Profil</label>
            <div className="mt-2"><PhotoUpload value={form.photo_path} onChange={(p) => setForm({ ...form, photo_path: p })} shape="circle" testId="my-profile-photo" /></div>
          </div>
          <F label="Nama Lengkap"><input value={form.name || ""} onChange={upd("name")} className="input-bizma" data-testid="my-name" /></F>
          <F label="Username / Panggilan"><input value={form.username || ""} onChange={upd("username")} className="input-bizma" placeholder="Bisa dipakai login" data-testid="my-username" /></F>
          <F label="Email"><input value={form.email || ""} disabled className="input-bizma bg-bizma-muted cursor-not-allowed" data-testid="my-email" /></F>
          <F label="No. WhatsApp"><input value={form.whatsapp || ""} onChange={upd("whatsapp")} className="input-bizma" placeholder="6281234567890" data-testid="my-whatsapp" /></F>
          <F label="Alamat"><input value={form.address || ""} onChange={upd("address")} className="input-bizma" data-testid="my-address" /></F>
          <F label="Kecamatan">
            <select value={form.kecamatan || KECAMATAN_MAGELANG[0]} onChange={upd("kecamatan")} className="input-bizma" data-testid="my-kecamatan">
              {KECAMATAN_MAGELANG.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
          </F>
          <F label="Kabupaten"><input value="Magelang" disabled className="input-bizma bg-bizma-muted cursor-not-allowed" data-testid="my-kabupaten" /></F>
          <F label="Tanggal Lahir"><input type="date" value={form.birthdate || ""} onChange={upd("birthdate")} className="input-bizma" data-testid="my-birthdate" /></F>
          <F label="Bio"><textarea rows={3} value={form.bio || ""} onChange={upd("bio")} className="input-bizma h-auto py-3" data-testid="my-bio" /></F>
          <div className="pt-2 border-t border-bizma-border">
            <div className="text-sm font-medium">Sosial Media Saya <span className="text-xs text-bizma-subtext font-normal">(opsional)</span></div>
            <p className="text-xs text-bizma-subtext mt-1">Cukup masukkan username. Muncul sebagai icon di kartu profil kamu.</p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="my-socials">
              {PLATFORMS.map(({ key, label, Icon, color }) => (
                <label key={key} className="flex items-center gap-3 border border-bizma-border rounded-2xl px-3 py-2 bg-white">
                  <span className={`w-8 h-8 rounded-full ${color} grid place-items-center shrink-0`}><Icon size={14}/></span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-bizma-subtext">{label}</div>
                    <input value={form.socials?.[key] || ""} onChange={updSocial(key)}
                      placeholder="@username" className="w-full bg-transparent text-sm outline-none" data-testid={`my-social-${key}`} />
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button type="button" onClick={cancelEdit} className="btn-outline flex-1" data-testid="cancel-edit-btn"><X size={16}/> Batal</button>
            <button disabled={busy} className="btn-primary flex-1" data-testid="my-save-btn"><Save size={16}/> {busy?"Menyimpan...":"Simpan Perubahan"}</button>
          </div>
        </form>
        ) : (
        <div className="felt-card p-6 sm:p-8 lg:col-span-2 space-y-5" data-testid="my-profile-view">
          <div className="flex items-start gap-4 pb-5 border-b border-bizma-border">
            {u.photo_path ? (
              <img src={fileUrl(u.photo_path)} alt="" className="w-20 h-20 rounded-full object-cover ring-4 ring-bizma-sage/30" data-testid="view-photo" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-bizma-sage text-white grid place-items-center text-2xl font-semibold">{(u.name||"?").charAt(0)}</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xl font-display font-semibold flex items-center gap-2">
                {u.name || "-"}
                {u.is_verified && <VerifiedBadge size={18} />}
              </div>
              {u.username && <div className="text-sm text-bizma-subtext">@{u.username}</div>}
              {u.bio && <p className="mt-2 text-sm text-[#4A4743] leading-relaxed">{u.bio}</p>}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div className="flex items-start gap-3">
              <span className="text-bizma-sage mt-0.5 shrink-0"><Mail size={14}/></span>
              <div className="min-w-0 flex-1">
                <div className="text-xs text-bizma-subtext uppercase tracking-widest flex items-center gap-2">
                  <span>Email</span>
                  {u.is_email_verified ? (
                    <span className="text-[10px] text-bizma-sage font-bold" data-testid="email-verified-badge">✓ Terverifikasi</span>
                  ) : (
                    <button onClick={async () => {
                      try { await api.post("/auth/resend-verification"); toast.success("Kode verifikasi terkirim"); window.location.href = "/verify-email"; }
                      catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
                    }} className="text-[10px] px-2 py-0.5 rounded-full bg-bizma-terracotta text-white font-bold hover:bg-opacity-90" data-testid="verify-email-btn">Verifikasi Email</button>
                  )}
                </div>
                <div className="text-sm font-medium truncate">{u.email || "-"}</div>
              </div>
            </div>
            <ViewRow icon={<MessageCircle size={14}/>} label="WhatsApp" value={u.whatsapp} />
            <ViewRow icon={<MapPin size={14}/>} label="Alamat" value={u.address} />
            <ViewRow icon={<MapPin size={14}/>} label="Kecamatan" value={u.kecamatan ? `${u.kecamatan}, Magelang` : "-"} />
            <ViewRow icon={<Calendar size={14}/>} label="Tanggal Lahir" value={u.birthdate ? new Date(u.birthdate).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" }) : "-"} />
            <ViewRow icon={<User size={14}/>} label="Kabupaten" value="Magelang" />
          </div>
          {(u.socials && Object.values(u.socials).some(v => v)) && (
            <div className="pt-4 border-t border-bizma-border">
              <div className="text-xs uppercase tracking-widest text-bizma-sage font-bold mb-3">Sosial Media</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {PLATFORMS.filter(p => u.socials?.[p.key]).map(({ key, label, Icon, color }) => (
                  <div key={key} className="flex items-center gap-3 border border-bizma-border rounded-2xl px-3 py-2">
                    <span className={`w-8 h-8 rounded-full ${color} grid place-items-center shrink-0`}><Icon size={14}/></span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-bizma-subtext">{label}</div>
                      <div className="text-sm truncate">{u.socials[key]}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        )}

        <div className="felt-card p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-semibold text-lg flex items-center gap-2"><Store size={18}/> Bisnis Saya</h2>
            <Link to="/app/businesses/new" className="btn-primary text-xs py-1.5 px-3" data-testid="add-biz-btn"><Plus size={14}/> Tambah</Link>
          </div>
          <div className="mt-4 space-y-3">
            {mine.length === 0 && <div className="text-sm text-bizma-subtext">Belum ada bisnis.</div>}
            {mine.map(b => (
              <div key={b.business_id} className="flex items-center gap-3 border border-bizma-border rounded-2xl p-3 hover:bg-bizma-muted transition-colors" data-testid={`my-business-${b.business_id}`}>
                <Link to={`/app/businesses/${b.business_id}`} className="flex items-center gap-3 flex-1 min-w-0" data-testid={`my-business-link-${b.business_id}`}>
                  <div className="w-14 h-14 rounded-xl bg-bizma-muted overflow-hidden shrink-0">
                    {b.photo_path && <img src={fileUrl(b.photo_path)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{b.name}</div>
                    <div className="text-xs text-bizma-subtext">{b.category}</div>
                    <div className="mt-1 flex items-center gap-3 text-xs text-bizma-sage">
                      <span className="inline-flex items-center gap-1"><Eye size={12}/> {b.views || 0}</span>
                      <span className="inline-flex items-center gap-1"><TrendingUp size={12}/> {b.wa_clicks || 0}</span>
                    </div>
                  </div>
                </Link>
                <Link to={`/app/businesses/${b.business_id}/edit`} className="text-bizma-subtext hover:text-bizma-terracotta p-2 shrink-0" data-testid={`edit-my-biz-${b.business_id}`}><Pencil size={16}/></Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function F({ label, children }) {
  return <div><label className="text-sm font-medium">{label}</label><div className="mt-2">{children}</div></div>;
}

function ViewRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-bizma-sage mt-0.5 shrink-0">{icon}</span>
      <div className="min-w-0">
        <div className="text-xs text-bizma-subtext uppercase tracking-widest">{label}</div>
        <div className="text-sm font-medium truncate">{value || "-"}</div>
      </div>
    </div>
  );
}
